/**
 * 네이티브 HTML5 DnD 기반 리스트 순서 변경.
 *
 * erp-fe·mes-ax-fe 가 각각 복제해 쓰던 구현을 코어로 승격한 것이다
 * (설계: docs/designs/core-document-template-engine.md §7.6.1).
 *
 * - 드래그 중에는 `displayRows` 만 재배열하고, 확정은 `onDragEnd` 의 `onCommit` 에서 한다.
 * - `onCommit` 이 실패하면 원래 순서로 되돌린다.
 * - HTML5 DnD 는 드롭 후 click 을 발생시키지 않으므로 호출부가
 *   `shouldSkipClick()`/`resetClickGuard()` 로 "드래그 직후 클릭 삼킴" 을 처리한다.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';

export type DragReorderRow = { id: string };

type DragState = {
  draggedId: string;
  orderIds: string[];
  moved: boolean;
};

export function useDragReorderList<T extends DragReorderRow>(
  source: Ref<T[]>,
  onCommit: (orderedIds: string[]) => Promise<void>,
): {
  displayRows: ComputedRef<T[]>;
  draggingId: ComputedRef<string>;
  dragMoved: Ref<boolean>;
  onDragStart: (row: T, event: DragEvent) => void;
  onDragOverRow: (row: T, event: DragEvent) => void;
  onDragOverList: (event: DragEvent) => void;
  onDragEnd: () => Promise<void>;
  shouldSkipClick: () => boolean;
  resetClickGuard: () => void;
} {
  const drag = ref<DragState | null>(null);
  const dragMoved = ref(false);
  const clickGuard = ref(false);

  const displayRows = computed(() => {
    if (!drag.value) return source.value;
    return drag.value.orderIds
      .map((id) => source.value.find((row) => row.id === id))
      .filter((row): row is T => Boolean(row));
  });

  const draggingId = computed(() => drag.value?.draggedId ?? '');

  function onDragStart(row: T, event: DragEvent): void {
    drag.value = {
      draggedId: row.id,
      orderIds: source.value.map((item) => item.id),
      moved: false,
    };
    dragMoved.value = false;
    clickGuard.value = false;
    event.dataTransfer?.setData('text/plain', row.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
    }
  }

  function moveBeforeTarget(targetId: string, after: boolean): void {
    if (!drag.value || drag.value.draggedId === targetId) return;
    const ids = drag.value.orderIds.filter((id) => id !== drag.value!.draggedId);
    let index = ids.indexOf(targetId);
    if (index < 0) return;
    if (after) index += 1;
    ids.splice(index, 0, drag.value.draggedId);
    const prev = drag.value.orderIds.join('|');
    drag.value.orderIds = ids;
    if (prev !== ids.join('|')) {
      drag.value.moved = true;
      dragMoved.value = true;
    }
  }

  function onDragOverRow(row: T, event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const el = event.currentTarget;
    if (!(el instanceof HTMLElement)) return;
    const rect = el.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2;
    moveBeforeTarget(row.id, after);
  }

  function onDragOverList(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  async function onDragEnd(): Promise<void> {
    const state = drag.value;
    drag.value = null;
    if (!state) return;

    const originalRows = [...source.value];
    const originalIds = originalRows.map((row) => row.id);
    const nextIds = state.orderIds;
    const changed = originalIds.join('|') !== nextIds.join('|');

    if (!changed) {
      clickGuard.value = dragMoved.value;
      return;
    }

    source.value = nextIds
      .map((id) => originalRows.find((row) => row.id === id))
      .filter((row): row is T => Boolean(row));

    try {
      await onCommit(nextIds);
    } catch {
      source.value = originalRows;
      throw new Error('reorder failed');
    } finally {
      clickGuard.value = true;
    }
  }

  function shouldSkipClick(): boolean {
    return clickGuard.value || dragMoved.value;
  }

  function resetClickGuard(): void {
    clickGuard.value = false;
    dragMoved.value = false;
  }

  return {
    displayRows,
    draggingId,
    dragMoved,
    onDragStart,
    onDragOverRow,
    onDragOverList,
    onDragEnd,
    shouldSkipClick,
    resetClickGuard,
  };
}
